import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ArrowRight, AlertCircle } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Input, Checkbox } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth, isAdmin } from '@/providers/AuthProvider';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signIn, isMockAuth } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error: err, profile } = await signIn(email, password);
    setLoading(false);
    if (err || !profile) {
      setError(err ?? 'Sign-in failed');
      return;
    }
    const dest = next ?? (isAdmin(profile.role) ? '/admin/dashboard' : '/portal/dashboard');
    nav(dest);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12 lg:py-20">
        <div className="w-full max-w-md">
          <Logo size="lg" />
          <h1 className="mt-8 font-display text-3xl font-bold tracking-tight text-ink">Welcome back</h1>
          <p className="mt-2 text-sm text-ink-muted">Sign in to your Locum Network account.</p>

          {isMockAuth && (
            <Badge tone="accent" className="mt-5">
              Mock-mode: any email signs you in. Try sarah.chen@example.com (doctor) or priya.kumar@locumnetwork.com.au (admin).
            </Badge>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <Input
              label="Email"
              type="email"
              required
              autoComplete="email"
              iconLeft={<Mail className="h-4 w-4" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              iconLeft={<Lock className="h-4 w-4" />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={isMockAuth ? 'Mock auth: any password works.' : undefined}
            />

            <div className="flex items-center justify-between text-sm">
              <Checkbox label="Remember me" />
              <Link to="/forgot-password" className="link">Forgot password?</Link>
            </div>

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                <AlertCircle className="h-3.5 w-3.5" /> {error}
              </div>
            )}

            <Button type="submit" loading={loading} fullWidth size="lg" iconRight={<ArrowRight className="h-4 w-4" />}>
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink-muted text-center">
            Don't have an account? <Link to="/register" className="link">Register as a doctor</Link>
          </p>
        </div>
      </div>
      <aside className="hidden lg:flex relative items-center justify-center bg-gradient-to-br from-primary-soft via-bg to-accent-soft p-12 overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-50">
          <div className="absolute -top-20 -left-10 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
        </div>
        <div className="max-w-md text-ink">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Why Locum Network</p>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight">
            Compliance, made effortless.
          </h2>
          <p className="mt-4 text-base text-ink-muted">
            One screen for your AHPRA, indemnity, police check, visa and more — with AI
            classification and a real human reviewer in the loop.
          </p>
        </div>
      </aside>
    </div>
  );
}
